import { FormEvent, useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { createDonationIntent, DonationFrequency } from '../api/donations';

type Props = {
  amountCents: number;
  frequency: DonationFrequency;
  minAmountCents: number;
  currency: string;
};

/**
 * The card form and submit button. Runs Stripe's deferred-intent flow:
 * validate + tokenize locally with `elements.submit()`, ask the server to create
 * the PaymentIntent (one-time) or Subscription (monthly) on the connected
 * account, then confirm with the returned client secret. On success Stripe
 * redirects to the `return_url`; on failure we surface the message inline.
 *
 * Must be rendered inside <Elements> — useStripe/useElements read that context.
 */
export default function DonationForm({
  amountCents,
  frequency,
  minAmountCents,
  currency,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const belowMinimum = amountCents < minAmountCents;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;
    setError(null);

    if (belowMinimum) {
      setError(`Minimum gift is ${formatMoney(minAmountCents, currency)}.`);
      return;
    }

    setSubmitting(true);
    try {
      // Validate + collect payment details before creating anything server-side.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? 'Please check your payment details.');
        return;
      }

      const { client_secret } = await createDonationIntent({
        amount_cents: amountCents,
        frequency,
        donor_email: donorEmail,
        donor_name: donorName,
      });

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: `${window.location.origin}/donate/success`,
          receipt_email: donorEmail || undefined,
        },
      });
      // confirmPayment only returns on failure; success is a redirect.
      if (confirmError) {
        setError(confirmError.message ?? 'Payment could not be completed.');
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="donate-form" onSubmit={handleSubmit}>
      <div className="donate-fields">
        <label className="donate-field">
          <span>Name</span>
          <input
            type="text"
            autoComplete="name"
            value={donorName}
            onChange={(event) => setDonorName(event.target.value)}
            data-testid="donate-name-input"
          />
        </label>
        <label className="donate-field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            value={donorEmail}
            onChange={(event) => setDonorEmail(event.target.value)}
            data-testid="donate-email-input"
          />
        </label>
      </div>

      <PaymentElement />

      {error && (
        <p className="donate-form-error" role="alert" data-testid="donate-form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="btn btn-primary donate-submit"
        disabled={!stripe || submitting || belowMinimum}
        data-testid="donate-submit-button"
      >
        {submitting
          ? 'Processing…'
          : `Give ${formatMoney(amountCents, currency)}${frequency === 'monthly' ? '/mo' : ''}`}
      </button>
    </form>
  );
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: Number.isInteger(cents / 100) ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
